import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, BookOpen, Clock, ArrowRight } from "lucide-react";
import { ARTICLES, CATEGORIES } from "../../data/learningArticles";
import { useDocMeta } from "../../lib/useDocMeta";
import { track, EVENT } from "../../lib/publicAnalytics";

export default function Learning() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  useDocMeta({
    title: "GovBidIQ Academy · Learning Center",
    description: "Free public education on federal contracting, bid/no-bid decisions, capture management, proposal compliance, and set-aside strategy.",
    path: "/learning",
  });

  useEffect(() => { track(EVENT.LEARNING_CENTER_VIEWED, {}); }, []);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    return ARTICLES.filter((a) =>
      (cat === "All" || a.category === cat) &&
      (!q || (a.title + " " + a.summary + " " + a.category).toLowerCase().includes(ql))
    );
  }, [q, cat]);

  return (
    <div className="max-w-[1300px] mx-auto px-6 py-14">
      <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-3">/ GovBidIQ Academy</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl text-slate-100 leading-tight mb-3">Learn the contracting craft.</h1>
      <p className="text-slate-400 text-lg max-w-2xl leading-relaxed mb-8">
        Plain-English explainers on FAR, NAICS, SAM.gov, CPARS, set-aside strategy, capture, and proposal craft. Free. No signup.
      </p>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search lessons…"
            data-testid="learning-search"
            aria-label="Search learning articles"
            className="w-full bg-[var(--bg-secondary)] border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm pl-10 pr-3 py-2.5 font-mono"
          />
        </div>
        <select
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          data-testid="learning-category"
          aria-label="Filter by category"
          className="bg-[var(--bg-secondary)] border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm px-3 py-2.5 font-mono"
        >
          <option value="All">All Categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-slate-500 font-mono text-sm">No lessons match your search.</div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border-soft)]">
        {filtered.map((a) => (
          <Link
            key={a.slug}
            to={`/learning/${a.slug}`}
            data-testid={`learning-card-${a.slug}`}
            className="bg-[var(--bg-secondary)] p-5 hover:bg-slate-900/70 transition-colors flex flex-col group"
          >
            <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-2 flex items-center gap-1.5">
              <BookOpen className="w-3 h-3" /> {a.category}
            </div>
            <h2 className="font-display font-bold text-slate-100 text-base mb-1.5 leading-snug group-hover:text-sky-300">{a.title}</h2>
            <p className="text-sm text-slate-400 leading-relaxed flex-1">{a.summary}</p>
            <div className="mt-4 flex items-center justify-between text-[11px] font-mono text-slate-500">
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {a.read_minutes} min read</span>
              <span className="inline-flex items-center gap-1 text-sky-400 group-hover:text-sky-300">Open <ArrowRight className="w-3 h-3" /></span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
